import * as repository from '../repository/orderRepository';
import * as orderItemRepository from '../repository/orderItemRepository';
import { CreateOrderParams, OrderItem, Order, PaymentMethod } from '../types/order';
import { DashboardSummary, DashboardSummaryDays, CustomerMonthlyTotals } from '../types/dashboardSummary';
import { DashboardSuggestions, SearchedOrder, OrderSearchMatchType } from '../types/orderSearch';

type OrderItemInput = {
  itemName: string;
  price: number;
  quantity: number;
};

class OrderServiceError extends Error {
  constructor(message: string, public cause?: Error) {
    super(message);
    this.name = 'OrderServiceError';
  }
}

/**
 * 새로운 주문과 첫 상품을 생성합니다.
 * FE precondition: items.length_before === 0 → items.length_after === 1
 */
export async function createWithItem(
  userId: string,
  nickname: string,
  orderDate: string,
  item: OrderItemInput
): Promise<number> {
  try {
    const totalPrice = item.price * item.quantity;

    const orderParams: CreateOrderParams = {
      userId,
      nickname,
      orderDate,
      totalPrice
    };

    const orderId = await repository.create(orderParams);

    try {
      await orderItemRepository.create({
        orderId,
        itemName: item.itemName,
        price: item.price,
        quantity: item.quantity
      });
    } catch (error) {
      // 상품 생성 실패 시 주문도 롤백
      await repository.deleteById(orderId);
      throw new OrderServiceError('상품 생성 실패로 인한 주문 롤백', error as Error);
    }

    return orderId;
  } catch (error) {
    throw new OrderServiceError('주문 생성 실패', error as Error);
  }
}

/**
 * 상품 없이 새로운 빈 주문을 생성합니다.
 */
export async function createEmptyOrder(
  userId: string,
  nickname: string,
  orderDate: string
): Promise<number> {
  try {
    const orderParams: CreateOrderParams = {
      userId,
      nickname,
      orderDate,
      totalPrice: 0, // 빈 주문이므로 총액은 0
    };

    const orderId = await repository.create(orderParams);

    return orderId;
  } catch (error) {
    throw new OrderServiceError('빈 주문 생성 실패', error as Error);
  }
}

/**
 * 기존 주문에 새로운 상품을 추가합니다.
 * FE precondition: items.length_before ≥ 1 AND items.length_after > items.length_before
 */
export async function addItem(
  orderId: number,
  item: OrderItemInput
): Promise<void> {
  try {
    console.log('상품 추가 시도', orderId, item);
    await orderItemRepository.create({
      orderId,
      itemName: item.itemName,
      price: item.price,
      quantity: item.quantity
    });

    await recalcTotal(orderId);
  } catch (error) {
    console.error('상품 추가 실패', error);
    throw new OrderServiceError('상품 추가 실패', error as Error);
  }
}

/**
 * 주문에서 상품을 제거합니다.
 * FE precondition: items.length_before > 1 AND items.length_after < items.length_before
 */
export async function removeItem(
  orderId: number,
  itemId: number
): Promise<void> {
  try {
    await orderItemRepository.deleteById(itemId);
    await recalcTotal(orderId);
  } catch (error) {
    throw new OrderServiceError('상품 제거 실패', error as Error);
  }
}

/**
 * 주문을 삭제합니다. 호출자는 정산서 고객 삭제뿐(마지막 상품 삭제는 removeItem으로 주문을 남긴다).
 * 연결된 배송엑셀 행(parsed_order)은 FK ON DELETE CASCADE로 함께 삭제된다.
 */
export async function removeOrder(
  orderId: number
): Promise<void> {
  try {
    await repository.deleteById(orderId);
  } catch (error) {
    throw new OrderServiceError('주문 삭제 실패', error as Error);
  }
}

/**
 * 주문에서 모든 상품을 제거합니다. (주문 자체는 남김)
 */
export async function removeAllItemsFromOrder(
  orderId: number
): Promise<void> {
  try {
    await orderItemRepository.deleteByOrderId(orderId);
    // 직접 patchTotalPrice(0) 대신 recalcTotal 경유 — 전체 삭제 후 재계산은 정의상 0으로 동일하고,
    // §16.10 bounded retry가 delete-커밋 후 patch 실패의 durable 불일치를 1회 자가 복구한다.
    await recalcTotal(orderId);
  } catch (error) {
    throw new OrderServiceError('주문 상품 전체 삭제 실패', error as Error);
  }
}


/**
 * 상품의 여러 정보를 한번에 업데이트합니다.
 * 가격이나 수량이 변경되면 주문 총액을 재계산합니다.
 */
export async function updateItem(
  orderId: number,
  itemId: number,
  data: { itemName?: string; price?: number; quantity?: number }
): Promise<void> {
  try {
    const updateData: { item_name?: string; price?: number; quantity?: number } = {};
    if (data.itemName !== undefined) updateData.item_name = data.itemName;
    if (data.price !== undefined) updateData.price = data.price;
    if (data.quantity !== undefined) updateData.quantity = data.quantity;

    await orderItemRepository.update(itemId, updateData);

    // 가격이나 수량이 변경된 경우에만 총액 재계산
    if (data.price !== undefined || data.quantity !== undefined) {
      await recalcTotal(orderId);
    }
  } catch (error) {
    throw new OrderServiceError('상품 정보 업데이트 실패', error as Error);
  }
}

/**
 * 다른 '미지정' 주문들에서 (상품명, 가격)이 수정 전 값과 일치하는 상품의 이름/가격을 일괄 수정합니다.
 * 스코프 날짜(활성 주문의 order_date) 도출·미지정 필터·원본 매칭·총액 재계산 전부를
 * 서버 RPC가 행 잠금 하에 한 트랜잭션에서 원자적으로 수행 — 조회→수정 2단계의 상태 변경 창 없음
 * ('미지정'만 전파는 입금액≠주문액 불일치 방지 불변식).
 * 수량은 고객별 값이라 전파하지 않는다(RPC에 파라미터 자체가 없음).
 * 반환 카운트는 주문 단위(셀러 멘탈모델 = "고객 N명").
 */
export async function bulkUpdateSameProductItems(params: {
  excludeOrderId: number;
  match: { itemName: string; price: number };
  changes: { itemName?: string; price?: number };
}): Promise<{ appliedOrderCount: number; appliedItemCount: number }> {
  try {
    return await orderItemRepository.bulkUpdateSameProduct({
      excludeOrderId: params.excludeOrderId,
      matchItemName: params.match.itemName,
      matchPrice: params.match.price,
      newItemName: params.changes.itemName,
      newPrice: params.changes.price,
    });
  } catch (error) {
    throw new OrderServiceError('동일 상품 일괄 수정 실패', error as Error);
  }
}

/**
 * 주문의 닉네임을 업데이트합니다.
 */
export async function updateOrderNickname(
  orderId: number,
  newNickname: string
): Promise<void> {
  try {
    await repository.patchNickname(orderId, newNickname);
  } catch (error) {
    throw new OrderServiceError('주문 닉네임 업데이트 실패', error as Error);
  }
}

/**
 * 특정 사용자의 특정 날짜 주문들을 조회합니다.
 */
export async function findByUserAndDate(userId: string, orderDate: string): Promise<Order[]> {
  try {
    return await repository.findByUserAndDate(userId, orderDate);
  } catch (error) {
    throw new OrderServiceError('주문 조회 실패', error as Error);
  }
}

/**
 * 특정 사용자의 특정 날짜 주문들과 관련 상품들을 한 번에 조회합니다.
 * 주문별 단건 상품 조회(N+1)를 피하기 위해 상품을 배치(IN) 조회 후 주문에 매핑합니다.
 */
export async function findByUserAndDateWithItems(userId: string, orderDate: string): Promise<Order[]> {
  try {
    const orders = await repository.findByUserAndDate(userId, orderDate);
    if (orders.length === 0) return [];

    const orderIds = orders.map(o => o.id);
    const allItems = await orderItemRepository.findByOrderIds(orderIds);

    const orderIdToItems: Record<number, typeof allItems> = {};
    for (const item of allItems) {
      if (!orderIdToItems[item.order_id]) orderIdToItems[item.order_id] = [];
      orderIdToItems[item.order_id].push(item);
    }

    return orders.map(order => ({ ...order, items: orderIdToItems[order.id] || [] }));
  } catch (error) {
    throw new OrderServiceError('날짜별 주문 조회 실패', error as Error);
  }
}

/**
 * 주문 상품들의 총 가격을 계산하는 헬퍼 함수
 */
function calculateTotalPrice(items: OrderItem[]): number {
  return items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
}

// 새로운 헬퍼 함수: OrderItemInput 배열로부터 총 가격 계산
function calculateTotalPriceForInputItems(items: OrderItemInput[]): number {
  return items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
}

/**
 * 주문의 총 가격을 재계산하고 업데이트하는 헬퍼 함수.
 * §16.10 total_price 쓰기 복구: 실패 시 find→calculate→patch 전체를 재실행하는 bounded retry(1회).
 * 캐시된 total 재전송 금지 — 실패~재시도 사이 다른 뮤테이션이 커밋한 items를 stale 값으로 덮어쓴다.
 * (부분입금 자동 전환 트리거가 이 patch의 UPDATE에 얹혀 재고 캐스케이드 락 경합이 늘 수 있음 — §16.12.)
 * 재시도 소진 후 durable 불일치는 재고 audit cron의 total_price<>SUM 검사가 가시화.
 */
async function recalcTotal(orderId: number): Promise<void> {
  const MAX_ATTEMPTS = 2;
  for (let attempt = 1; ; attempt++) {
    try {
      const items = await orderItemRepository.findByOrderId(orderId);
      const totalPrice = calculateTotalPrice(items);
      await repository.patchTotalPrice(orderId, totalPrice);
      return;
    } catch (error) {
      if (attempt >= MAX_ATTEMPTS) {
        throw new OrderServiceError('총 가격 재계산 실패', error as Error);
      }
    }
  }
}

/**
 * 다중 상품으로 주문을 생성합니다.
 * FE precondition: items.length_before === 0 → items.length_after === items.length_before
 */
export async function createOrderWithMultipleItems(
  userId: string,
  nickname: string,
  orderDate: string,
  items: OrderItemInput[],
  messageTemplate?: string
): Promise<number> {
  try {
    const totalPrice = calculateTotalPriceForInputItems(items);

    const orderParams: CreateOrderParams & { message_template?: string } = {
      userId,
      nickname,
      orderDate,
      totalPrice,
    };
    if (messageTemplate) {
      orderParams.message_template = messageTemplate;
    }

    // repository.create가 orderParams의 모든 속성을 처리한다고 가정
    const orderId = await repository.create(orderParams);

    for (const item of items) {
      try {
        await orderItemRepository.create({
          orderId,
          itemName: item.itemName,
          price: item.price,
          quantity: item.quantity,
        });
      } catch (itemError) {
        // 아이템 생성 실패 시 로그를 남기고 계속 진행하거나,
        // 전체 롤백 로직을 구현할 수 있습니다. (현재는 로그만 남김)
        console.error(`OrderService: Failed to create item ${item.itemName} for order ${orderId}`, itemError);
        // 필요하다면 여기서 생성된 orderId를 사용하여 주문 자체를 삭제하는 등의 롤백 로직 추가
      }
    }
    return orderId;
  } catch (error) {
    throw new OrderServiceError('다중 상품으로 주문 생성 실패', error as Error);
  }
}

/**
 * 특정 닉네임의 최근 2주 주문을 조회합니다.
 */
export async function findRecentRelevantOrdersByNickname(
  userId: string,
  nickname: string,
  currentDate: Date,
  excludeOrderId?: number | null
): Promise<Order[]> {
  try {
    const orders = await repository.findRecentRelevantOrdersByNickname(userId, nickname, currentDate, excludeOrderId);
    
    // For each order, fetch its items and attach them
    const ordersWithItems = await Promise.all(
      orders.map(async (order) => {
        const items = await orderItemRepository.findByOrderId(order.id);
        return { ...order, items };
      })
    );

    return ordersWithItems;
  } catch (error) {
    throw new OrderServiceError('최근 주문 및 상품 조회 실패', error as Error);
  }
}

/**
 * 주문의 is_keep 상태를 업데이트합니다.
 */
export async function updateOrderKeepStatus(orderId: number, isKeep: boolean): Promise<void> {
  try {
    await repository.patchIsKeep(orderId, isKeep);
  } catch (error) {
    throw new OrderServiceError('주문 킵 상태 업데이트 실패', error as Error);
  }
}

/**
 * 특정 날짜의 킵(is_keep) 주문 닉네임 목록을 조회합니다. (배송엑셀 킵 배지/킵 제외 다운로드용)
 */
export async function getKeepNicknamesByDate(userId: string, orderDate: string): Promise<string[]> {
  try {
    return await repository.findKeepNicknamesByDate(userId, orderDate);
  } catch (error) {
    throw new OrderServiceError('킵 닉네임 조회 실패', error as Error);
  }
}

/**
 * 주문 단건의 status를 조회합니다. (부분입금 자동 전환 reconcile용, 스펙 §16.10)
 */
export async function getOrderStatus(orderId: number): Promise<string | null> {
  try {
    return await repository.findStatusById(orderId);
  } catch (error) {
    throw new OrderServiceError('주문 상태 조회 실패', error as Error);
  }
}

/**
 * 주문의 status를 업데이트합니다.
 */
export async function updateOrderStatus(orderId: number, status: string): Promise<void> {
  try {
    await repository.patchStatus(orderId, status);
  } catch (error) {
    throw new OrderServiceError('주문 상태 업데이트 실패', error as Error);
  }
}

/**
 * 주문의 메모를 업데이트합니다.
 */
export async function updateOrderMemo(orderId: number, memo: string): Promise<void> {
  try {
    await repository.patchMemo(orderId, memo);
  } catch (error) {
    throw new OrderServiceError('주문 메모 업데이트 실패', error as Error);
  }
}

/**
 * 주문의 결제수단을 업데이트합니다.
 */
export async function updateOrderPaymentMethod(orderId: number, paymentMethod: PaymentMethod | null): Promise<void> {
  try {
    await repository.patchPaymentMethod(orderId, paymentMethod);
  } catch (error) {
    throw new OrderServiceError('주문 결제수단 업데이트 실패', error as Error);
  }
}

/**
 * 주문의 shipping_fee_override를 업데이트합니다. (null | 0 | 양수)
 */
export async function updateShippingFeeOverride(orderId: number, value: number | null): Promise<void> {
  try {
    await repository.patchShippingFeeOverride(orderId, value);
  } catch (error) {
    throw new OrderServiceError('주문 배송비 오버라이드 업데이트 실패', error as Error);
  }
}


/**
 * 대시보드 요약을 조회합니다. (서버 집계 — 상태별 count/리스트 + 킵 아이템 집계)
 * @param days 과거 조회일수 (30/60/90 — 서버에서 클램프)
 */
export async function getDashboardSummary(days: DashboardSummaryDays): Promise<DashboardSummary> {
  try {
    return await repository.fetchDashboardSummary(days);
  } catch (error) {
    throw new OrderServiceError('대시보드 요약 조회 실패', error as Error);
  }
}

export async function getTopProductsByMonth(month: string, limit: number) {
  try {
    return await repository.fetchTopProductsByMonth(month, limit);
  } catch (error) {
    throw new OrderServiceError('많이 주문된 상품 조회 실패', error as Error);
  }
}

export async function getCustomerMonthlyTotals(month: string): Promise<CustomerMonthlyTotals> {
  try {
    return await repository.fetchCustomerMonthlyTotals(month);
  } catch (error) {
    throw new OrderServiceError('고객별 구매 총액 조회 실패', error as Error);
  }
}

/**
 * 닉네임 부분일치로 주문과 관련 상품들을 조회합니다. (닉네임 매칭 전용 — 상품명 매칭 포함하지 않음)
 * 검색 윈도우는 서버 KST -90~+30일 (RPC가 보장).
 */
export async function findOrdersByNicknameWithItems(userId: string, q: string): Promise<{ orders: Order[]; isPartial: boolean }> {
  try {
    const { orderIds, isPartial: rpcPartial } = await repository.searchOrderIdsByNickname(q);
    if (orderIds.length === 0) return { orders: [], isPartial: rpcPartial };

    const { orders, isPartial: ordersPartial } = await repository.findOrdersByIds(userId, orderIds);
    const { items: allItems, isPartial: itemsPartial } = await orderItemRepository.findByOrderIdsWithPartialCheck(orderIds);

    const orderIdToItems: Record<number, typeof allItems> = {};
    for (const item of allItems) {
      if (!orderIdToItems[item.order_id]) orderIdToItems[item.order_id] = [];
      orderIdToItems[item.order_id].push(item);
    }

    const hydrated = orders.map(order => ({ ...order, items: orderIdToItems[order.id] || [] }));

    // 정렬: order_date desc, id desc (findOrdersByIds는 청크 분할로 정렬 비보장 — 호출자 책임)
    hydrated.sort((a, b) => {
      if (a.order_date !== b.order_date) return a.order_date < b.order_date ? 1 : -1;
      return b.id - a.id;
    });

    return { orders: hydrated, isPartial: rpcPartial || ordersPartial || itemsPartial };
  } catch (error) {
    throw new OrderServiceError('닉네임으로 주문 검색 실패', error as Error);
  }
}

/**
 * 주문 날짜를 단건 변경합니다.
 */
export async function updateOrderDate(orderId: number, orderDate: string): Promise<void> {
  try {
    await repository.patchOrderDate(orderId, orderDate);
  } catch (error) {
    throw new OrderServiceError('주문 날짜 업데이트 실패', error as Error);
  }
}

/**
 * 여러 주문의 날짜를 일괄 변경합니다.
 */
export async function updateOrdersDate(orderIds: number[], orderDate: string): Promise<void> {
  try {
    await repository.patchOrderDateBulk(orderIds, orderDate);
  } catch (error) {
    throw new OrderServiceError('주문 날짜 일괄 업데이트 실패', error as Error);
  }
}
/**
 * 특정 시각(created_at) 이후 주문(정산서) 사용 이력이 있는지 확인합니다.
 */
export async function hasOrdersSince(userId: string, sinceIso: string): Promise<boolean> {
  try {
    return await repository.existsOrderSince(userId, sinceIso);
  } catch (error) {
    throw new OrderServiceError('주문 이력 확인 실패', error as Error);
  }
}

/**
 * 대시보드 통합 검색 자동완성 제안(고객 닉네임 + 상품명)을 조회합니다.
 */
export async function getDashboardSearchSuggestions(q: string): Promise<DashboardSuggestions> {
  try {
    return await repository.fetchDashboardSuggestions(q);
  } catch (error) {
    throw new OrderServiceError('대시보드 검색 제안 조회 실패', error as Error);
  }
}

/**
 * 상품명으로 주문을 검색하고 관련 상품들을 함께 조회합니다. (상품명 매칭 전용 — 닉네임 매칭 포함하지 않음)
 */
export async function findOrdersByItemNameWithItems(userId: string, q: string): Promise<{ orders: SearchedOrder[]; isPartial: boolean }> {
  try {
    const { rows, isPartial: rpcPartial } = await repository.searchOrderIdsByItemName(q);
    if (rows.length === 0) return { orders: [], isPartial: rpcPartial };

    const orderIds = rows.map(row => row.orderId);
    const { orders, isPartial: ordersPartial } = await repository.findOrdersByIds(userId, orderIds);
    const { items: allItems, isPartial: itemsPartial } = await orderItemRepository.findByOrderIdsWithPartialCheck(orderIds);

    const orderIdToItems: Record<number, typeof allItems> = {};
    for (const item of allItems) {
      if (!orderIdToItems[item.order_id]) orderIdToItems[item.order_id] = [];
      orderIdToItems[item.order_id].push(item);
    }

    const matchedItemIdsByOrderId = new Map(rows.map(row => [row.orderId, row.matchedItemIds]));

    const searchedOrders: SearchedOrder[] = orders.map(order => ({
      ...order,
      items: orderIdToItems[order.id] || [],
      matchTypes: ['product'] as OrderSearchMatchType[],
      matchedItemIds: matchedItemIdsByOrderId.get(order.id) ?? [],
    }));

    // 정렬: order_date desc, id desc (order_date는 'yyyy-MM-dd' 문자열이라 단순 비교로 충분)
    searchedOrders.sort((a, b) => {
      if (a.order_date !== b.order_date) return a.order_date < b.order_date ? 1 : -1;
      return b.id - a.id;
    });

    return { orders: searchedOrders, isPartial: rpcPartial || ordersPartial || itemsPartial };
  } catch (error) {
    throw new OrderServiceError('상품명으로 주문 검색 실패', error as Error);
  }
}

/**
 * 닉네임 + 상품명을 함께 검색하는 통합 검색입니다. (대시보드 통합 검색)
 * 닉네임 매칭(부분일치 RPC)과 상품명 매칭(부분일치 RPC)을 병합 — 둘 다 매칭되면 matchTypes에 둘 다 포함.
 * 검색 윈도우는 양쪽 모두 서버 KST -90~+30일 (RPC가 보장).
 */
export async function findOrdersUnifiedWithItems(userId: string, q: string): Promise<{ orders: SearchedOrder[]; isPartial: boolean }> {
  try {
    const [
      { orderIds: nicknameOrderIdList, isPartial: nicknamePartial },
      { rows: productRows, isPartial: rpcPartial },
    ] = await Promise.all([
      repository.searchOrderIdsByNickname(q),
      repository.searchOrderIdsByItemName(q),
    ]);

    const nicknameOrderIds = new Set(nicknameOrderIdList);
    const matchedItemIdsByOrderId = new Map(productRows.map(row => [row.orderId, row.matchedItemIds]));

    // 양쪽 다 id-only RPC라 본문은 합집합으로 일괄 하이드레이션
    const allOrderIds = [...new Set([...nicknameOrderIdList, ...productRows.map(row => row.orderId)])];
    const { orders: loadedOrders, isPartial: findOrdersByIdsPartial } = await repository.findOrdersByIds(userId, allOrderIds);

    const mergedOrders: SearchedOrder[] = loadedOrders.map(order => {
      const matchedItemIds = matchedItemIdsByOrderId.get(order.id);
      const matchTypes: OrderSearchMatchType[] = [];
      if (nicknameOrderIds.has(order.id)) matchTypes.push('nickname');
      if (matchedItemIds !== undefined) matchTypes.push('product');
      return { ...order, matchTypes, matchedItemIds: matchedItemIds ?? [] };
    });

    // 정렬: order_date desc, id desc (order_date는 'yyyy-MM-dd' 문자열이라 단순 비교로 충분)
    mergedOrders.sort((a, b) => {
      if (a.order_date !== b.order_date) return a.order_date < b.order_date ? 1 : -1;
      return b.id - a.id;
    });

    const orderIds = mergedOrders.map(order => order.id);
    const { items: allItems, isPartial: itemsPartial } = await orderItemRepository.findByOrderIdsWithPartialCheck(orderIds);

    const orderIdToItems: Record<number, typeof allItems> = {};
    for (const item of allItems) {
      if (!orderIdToItems[item.order_id]) orderIdToItems[item.order_id] = [];
      orderIdToItems[item.order_id].push(item);
    }

    // 상품명 RPC LIMIT 500 잘림 시 닉네임∩상품 교집합 누락 보정 — 서버 ILIKE는 메타문자 escape 후
    // 리터럴 contains라 클라이언트 includes와 시맨틱 동일 (codexloop R1 #2).
    // RPC rows에 이미 있던 주문(matchedItemIdsByOrderId에서 값을 받은 주문)은 서버 값이 source of truth라 건드리지 않는다.
    // 게이트(codexloop R2 합의): rpcPartial=false면 RPC가 윈도우 내 전체 매칭을 반환한 것이라 보정이
    // 서버 판정과 모순될 수 있어 실행하지 않고, itemsPartial=true면 보정 입력(items)이 잘려 정확성을
    // 주장할 수 없어 실행하지 않는다. (닉네임도 동일 서버 KST 윈도우로 이관되어 기존 ±1일 경계 엣지는 해소)
    const shouldPatchIntersection = rpcPartial && !itemsPartial;
    // 대칭 케이스: 닉네임 RPC 잘림 시 상품-only 주문의 닉네임 배지 누락 보정.
    // order.nickname은 이미 하이드레이션된 본문이라 items 게이트 불필요.
    const shouldPatchNickname = nicknamePartial;
    const needle = q.trim().toLowerCase();
    const orders: SearchedOrder[] = mergedOrders.map(order => {
      const items = orderIdToItems[order.id] || [];
      let patched = order;
      if (shouldPatchNickname && needle !== '' && !patched.matchTypes.includes('nickname') && patched.nickname.toLowerCase().includes(needle)) {
        patched = { ...patched, matchTypes: ['nickname', ...patched.matchTypes] as OrderSearchMatchType[] };
      }
      if (shouldPatchIntersection && needle !== '' && patched.matchTypes.length === 1 && patched.matchTypes[0] === 'nickname') {
        const matchedItems = items.filter(item => item.item_name.toLowerCase().includes(needle));
        if (matchedItems.length > 0) {
          return {
            ...patched,
            items,
            matchTypes: ['nickname', 'product'] as OrderSearchMatchType[],
            matchedItemIds: matchedItems.map(item => item.id),
          };
        }
      }
      return { ...patched, items };
    });

    return { orders, isPartial: nicknamePartial || rpcPartial || findOrdersByIdsPartial || itemsPartial };
  } catch (error) {
    throw new OrderServiceError('통합 주문 검색 실패', error as Error);
  }
}

/**
 * 배송정보 상품명 stale 스캔 전용 order_item 조회 (무결성 계약 포함).
 * verified=false면 호출부는 스캔 실패로 처리해야 한다 — 잘린 데이터로 합성 금지.
 */
export async function getOrderItemsForSyncScan(
  orderIds: number[]
): Promise<{ items: OrderItem[]; verified: boolean }> {
  return orderItemRepository.findByOrderIdsForSyncScan(orderIds);
}
